import React, { useState, useEffect } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faPrint } from '@fortawesome/free-solid-svg-icons';
import './Stock.css';

import useTableSelection from './shared/useTableSelection';
import TableToolbar from './shared/TableToolbar';
import { exportToPrint, exportToExcel, exportToPDF } from './shared/exportTable';

const AllStock = ({ openEditDrawer, refreshTrigger }) => {
    const [stocksData, setStocksData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(null); // Track which stock is updating

    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const fetchStocks = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_stock.php', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                let fetchedStocks = res.data.data;

                // ROLE-BASED FILTERING
                if (userType === 'administrator') {
                    // Admin sees all
                } else if (userType === 'leader') {
                    let leadersGroupId = null;
                    for (const stock of fetchedStocks) {
                        if (stock.assigned_users && Array.isArray(stock.assigned_users)) {
                            const found = stock.assigned_users.find(u => String(u.id) === String(loggedInUserId));
                            if (found && stock.group_id) {
                                leadersGroupId = stock.group_id;
                                break;
                            }
                        }
                    }
                    if (leadersGroupId) {
                        fetchedStocks = fetchedStocks.filter(stock => stock.group_id === leadersGroupId);
                    } else {
                        fetchedStocks = [];
                    }
                } else if (userType === 'member') {
                    fetchedStocks = fetchedStocks.filter(stock => 
                        stock.assigned_users && Array.isArray(stock.assigned_users) &&
                        stock.assigned_users.some(u => String(u.id) === String(loggedInUserId))
                    );
                }

                setStocksData(fetchedStocks);
            } else {
                setStocksData([]);
            }
        } catch (err) { 
            console.error("Fetch Error:", err); 
            setStocksData([]); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        fetchStocks();
    }, [refreshTrigger]);

    // ===== FUNCTION TO UPDATE SHIPMENT STATUS =====
    const updateShipmentStatus = async (id, newStatus) => {
        setUpdatingStatus(id);
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/update_stock_status.php', { 
                id: id,
                shipment_status: newStatus
            }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
            fetchStocks();
        } catch (err) {
            console.error("Error updating shipment status:", err);
            alert("Error updating shipment status");
        } finally {
            setUpdatingStatus(null);
        }
    };

    // ===== FUNCTION TO UPDATE STATUS =====
    const updateStatus = async (id, newStatus) => {
        setUpdatingStatus(id);
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/update_stock_status.php', { 
                id: id,
                status: newStatus
            }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
            fetchStocks();
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Error updating status");
        } finally {
            setUpdatingStatus(null);
        }
    };

    const handleDelete = async (id) => {
        if (userType !== 'administrator') {
            alert("Only Super Admin can delete stock.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this stock?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/delete_stock.php', { id }, { headers: { Authorization: `Bearer ${token}` } });
            fetchStocks();
        } catch (err) {
            alert("Error deleting stock");
            console.error(err);
        }
    };

    const filteredStocks = stocksData.filter(stock => {
        const term = searchTerm.toLowerCase();
        return (
            (stock.stock_name || "").toLowerCase().includes(term) ||
            (stock.chassis_number || "").toLowerCase().includes(term) ||
            (stock.container_number || "").toLowerCase().includes(term) ||
            (stock.shipment_status || "").toLowerCase().includes(term) ||
            (stock.status || "").toLowerCase().includes(term)
        );
    });

    // Helper to get real end date or fallback
    const getEndDate = (stock) => {
        return stock.purchase_date || stock.created_at || 'N/A';
    };

    // Helper to get Client Name
        // Helper to get Client Name
    const getClientName = (stock) => {
        // If you link stock to a client later, use stock.client_name. 
        // For now, display 'Undefined Client' as per screenshot.
        return 'Undefined Client';
    };

    // ===== SELECTION =====
    const selection = useTableSelection(filteredStocks, (row) => row.id);

    // ===== EXPORT CONFIG =====
    const exportColumns = [
        'Stock Name',
        'Client',
        'End Date',
        'Chassis Number',
        'Container Number',
        'Shipment Status',
        'Assigned To',
        'Status',
    ];

    const formatAssignedTo = (stock) => {
        if (stock.assigned_to_type === 'everyone') return 'Everyone';
        if (!Array.isArray(stock.assigned_users) || stock.assigned_users.length === 0) return '';
        return stock.assigned_users.map(u => u.name).join(', ');
    };

    const buildExportRows = (rows) => rows.map(stock => [
        stock.stock_name || '',
        getClientName(stock),
        getEndDate(stock),
        stock.chassis_number || '',
        stock.container_number || '',
        stock.shipment_status || 'Waiting for shipment',
        formatAssignedTo(stock),
        stock.status || 'Started',
    ]);

    const handleExport = (kind) => {
        const rows = selection.selectedRows;
        if (rows.length === 0) return;
        const title = 'Stock Export';
        const data = buildExportRows(rows);
        if (kind === 'print') exportToPrint(title, exportColumns, data);
        if (kind === 'excel') exportToExcel(title, exportColumns, data);
        if (kind === 'pdf')   exportToPDF(title, exportColumns, data);
    };

    return (
        <div className="stock-list-container">
            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isSelectMode={selection.isSelectMode}
                onToggleSelectMode={selection.toggleSelectMode}
                onSelectAll={() =>
                    selection.allSelected ? selection.clearSelection() : selection.selectAll()
                }
                selectedCount={selection.selectedIds.size}
                allSelected={selection.allSelected}
                onPrint={() => handleExport('print')}
                onExcel={() => handleExport('excel')}
                onPDF={() => handleExport('pdf')}
            />

            <div className="table-responsive">
                <table className="stock-table">
                    <thead>
                        <tr>
                            {selection.isSelectMode && (
                                <th style={{ width: 42, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selection.allSelected}
                                        onChange={() =>
                                            selection.allSelected
                                                ? selection.clearSelection()
                                                : selection.selectAll()
                                        }
                                        title={selection.allSelected ? 'Unselect All' : 'Select All'}
                                    />
                                </th>
                            )}
                            <th>Stock Name</th>
                            <th>Client</th>
                            <th>End Date</th>
                            <th>Chassis Number</th>
                            <th>Container Number</th>
                            <th>Shipment Status</th>
                            <th>Assigned To</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="9" style={{textAlign:"center", padding: 40, color:"#888"}}>Loading stock...</td></tr>
                        ) : filteredStocks.length === 0 ? (
                            <tr><td colSpan="9" style={{textAlign:"center", padding: 40, color:"#888"}}>No stock found.</td></tr>
                        ) : (
                            filteredStocks.map(stock => (
                                <tr key={stock.id}>
                                    {selection.isSelectMode && (
                                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selection.isRowSelected(stock.id)}
                                                onChange={() => selection.toggleRow(stock.id)}
                                            />
                                        </td>
                                    )}
                                    <td style={{fontWeight: 600}}>{stock.stock_name}</td>
                                    <td>{getClientName(stock)}</td>
                                    <td>{getEndDate(stock)}</td>
                                    <td>{stock.chassis_number}</td>
                                    <td>{stock.container_number}</td>
                                    
                                    {/* Real Shipment Status + Functional Dropdown */}
                                    <td>
                                        <span className="status-badge orange">{stock.shipment_status || 'Waiting for shipment'}</span>
                                        <select 
                                            style={{ marginLeft: '5px', padding: '3px' }}
                                            value={stock.shipment_status || 'Waiting for shipment'}
                                            disabled={updatingStatus === stock.id}
                                            onChange={(e) => updateShipmentStatus(stock.id, e.target.value)}
                                        >
                                            <option>Waiting for shipment</option>
                                            <option>Shipped</option>
                                            <option>Surrendered</option>
                                        </select>
                                    </td>
                                    
                                    {/* Assigned Users */}
                                    <td>
                                        <div className="avatar-group">
                                            {stock.assigned_to_type === 'everyone' ? (
                                                <span style={{background: '#6f42c1', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500}}>Everyone</span>
                                            ) : (
                                                stock.assigned_users?.slice(0, 3).map((user, idx) => (
                                                    <div key={idx} style={{width: 30, height: 30, borderRadius: '50%', background: user.image ? 'transparent' : '#007bff', overflow: 'hidden', border: '2px solid #fff', marginLeft: '-5px'}}>
                                                        {user.image ? <img src={user.image} style={{width:'100%', height:'100%', objectFit:'cover'}} /> : <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'12px'}}>{user.name?.charAt(0)}</div>}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </td>
                                    
                                    {/* Real Status + Functional Dropdown */}
                                    <td>
                                        <span className="status-badge orange">{stock.status || 'Started'}</span>
                                        <select 
                                            style={{ marginLeft: '5px', padding: '3px' }}
                                            value={stock.status || 'Started'}
                                            disabled={updatingStatus === stock.id}
                                            onChange={(e) => updateStatus(stock.id, e.target.value)}
                                        >
                                            <option>Started</option>
                                            <option>In Progress</option>
                                            <option>In Hold</option>
                                            <option>Cancel</option>
                                            <option>Completed</option>
                                        </select>
                                    </td>
                                    
                                    <td>
                                        <div className="action-btns">
                                            <button className="action-icon-btn edit" onClick={() => openEditDrawer(stock.id)}>✎</button>
                                            <button className="action-icon-btn delete" onClick={() => handleDelete(stock.id)} disabled={userType !== 'administrator'}>🗑</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AllStock;